/**
 * 场次价格配置页面
 * 管理场次的4套价格体系
 */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  DatePicker,
  TimePicker,
  message,
  Space,
  Tag,
  Typography,
  Alert,
  Popconfirm,
} from 'antd'
import {
  ReloadOutlined,
  DollarOutlined,
  SaveOutlined,
  EditOutlined,
  TagOutlined,
  CrownOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { sessionPricingApi } from '@/services/api'
import type { SessionPricing } from '@/types/system'

const { Title, Text } = Typography

const SessionPricingPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [data, setData] = useState<SessionPricing[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [modalVisible, setModalVisible] = useState(false)
  const [editingRecord, setEditingRecord] = useState<SessionPricing | null>(null)
  const [form] = Form.useForm()

  // 获取场次列表
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await sessionPricingApi.getSessions({
        page,
        page_size: pageSize,
      })
      // 字段映射：API返回unit_price，但页面需要original_price
      const items = (response.data.items || []).map((item: any) => ({
        ...item,
        original_price: item.unit_price || 0,
        vip_price: item.vip_price || item.unit_price || 0,
        discount_price: item.discount_price || null,
        vip_discount_price: item.vip_discount_price || null,
        discount_enabled: item.discount_enabled || false,
        discount_start_time: item.discount_start_time || null,
        discount_end_time: item.discount_end_time || null,
      }))
      setData(items)
      setTotal(response.data.total || 0)
    } catch (error: any) {
      message.error(`获取场次列表失败: ${error.message || '未知错误'}`)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize])

  // 初始化
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // 打开编辑模态框
  const handleEdit = (record: SessionPricing) => {
    setEditingRecord(record)
    form.setFieldsValue({
      original_price: record.original_price,
      vip_price: record.vip_price,
      discount_price: record.discount_price,
      vip_discount_price: record.vip_discount_price,
      discount_enabled: record.discount_enabled,
      discount_start_time: record.discount_start_time ? dayjs(record.discount_start_time, 'HH:mm') : null,
      discount_end_time: record.discount_end_time ? dayjs(record.discount_end_time, 'HH:mm') : null,
    })
    setModalVisible(true)
  }

  // 提交表单
  const handleSubmit = async () => {
    if (!editingRecord) return

    try {
      const values = await form.validateFields()
      setSaving(true)

      const submitData = {
        original_price: values.original_price,
        vip_price: values.vip_price,
        discount_price: values.discount_price,
        vip_discount_price: values.vip_discount_price,
        discount_enabled: values.discount_enabled,
        discount_start_time: values.discount_start_time?.format('HH:mm') || null,
        discount_end_time: values.discount_end_time?.format('HH:mm') || null,
      }

      await sessionPricingApi.updatePricing(editingRecord.id, submitData)
      message.success('更新场次价格成功')
      setModalVisible(false)
      fetchData()
    } catch (error: any) {
      if (error.errorFields) return
      message.error(`保存失败: ${error.message || '未知错误'}`)
    } finally {
      setSaving(false)
    }
  }

  // 批量启用/禁用折扣
  const handleBatchToggleDiscount = async (enabled: boolean) => {
    Modal.confirm({
      title: enabled ? '批量启用折扣' : '批量禁用折扣',
      content: `确定要${enabled ? '启用' : '禁用'}所有场次的折扣时段吗？`,
      onOk: async () => {
        try {
          // 这里应该调用批量API，现在先单个处理
          for (const item of data) {
            await sessionPricingApi.updatePricing(item.id, {
              discount_enabled: enabled,
            })
          }
          message.success(`批量${enabled ? '启用' : '禁用'}折扣成功`)
          fetchData()
        } catch (error: any) {
          message.error(`操作失败: ${error.message || '未知错误'}`)
        }
      },
    })
  }

  // 表格列定义
  const columns = [
    {
      title: '场次ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '场次名称',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: '日期',
      dataIndex: 'session_date',
      key: 'session_date',
      width: 120,
      render: (text: string) => dayjs(text).format('YYYY-MM-DD'),
    },
    {
      title: '时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 100,
    },
    {
      title: '原价',
      dataIndex: 'original_price',
      key: 'original_price',
      width: 120,
      render: (price: number) => (
        <Tag icon={<DollarOutlined />}>
          ¥{((price || 0) / 100).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'VIP价',
      dataIndex: 'vip_price',
      key: 'vip_price',
      width: 120,
      render: (price: number) => (
        <Tag color="gold" icon={<CrownOutlined />}>
          ¥{((price || 0) / 100).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: '折扣价',
      dataIndex: 'discount_price',
      key: 'discount_price',
      width: 120,
      render: (price: number | null, record: SessionPricing) => (
        price ? (
          <Tag color="green" icon={<TagOutlined />}>
            ¥{((price || 0) / 100).toFixed(2)}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'VIP折扣价',
      dataIndex: 'vip_discount_price',
      key: 'vip_discount_price',
      width: 120,
      render: (price: number | null, record: SessionPricing) => (
        price ? (
          <Tag color="orange" icon={<CrownOutlined />}>
            ¥{((price || 0) / 100).toFixed(2)}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: '折扣时段',
      key: 'discount_period',
      width: 150,
      render: (_: any, record: SessionPricing) => (
        record.discount_enabled && record.discount_start_time && record.discount_end_time ? (
          <Tag color="blue">
            {record.discount_start_time} - {record.discount_end_time}
          </Tag>
        ) : (
          <Tag>未启用</Tag>
        )
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: SessionPricing) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          编辑
        </Button>
      ),
    },
  ]

  return (
    <div className="p-6">
      {/* 页面标题和操作栏 */}
      <Card className="mb-4">
        <Space className="w-full justify-between" wrap>
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <DollarOutlined className="text-green-500" />
              场次价格配置
            </h2>
            <p className="text-gray-500 text-sm">管理场次的4套价格体系（原价、VIP价、折扣价、VIP折扣价）</p>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              刷新
            </Button>
            <Button onClick={() => handleBatchToggleDiscount(true)}>
              批量启用折扣
            </Button>
            <Button onClick={() => handleBatchToggleDiscount(false)}>
              批量禁用折扣
            </Button>
          </Space>
        </Space>
      </Card>

      {/* 价格体系说明 */}
      <Alert
        message="4套价格体系说明"
        description={
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Tag icon={<DollarOutlined />}>原价</Tag>
              <div className="text-sm text-gray-500 mt-1">普通用户正常价格</div>
            </div>
            <div>
              <Tag color="gold" icon={<CrownOutlined />}>VIP价</Tag>
              <div className="text-sm text-gray-500 mt-1">VIP用户专享价格</div>
            </div>
            <div>
              <Tag color="green" icon={<TagOutlined />}>折扣价</Tag>
              <div className="text-sm text-gray-500 mt-1">普通用户折扣时段价格</div>
            </div>
            <div>
              <Tag color="orange" icon={<CrownOutlined />}>VIP折扣价</Tag>
              <div className="text-sm text-gray-500 mt-1">VIP用户折扣时段价格</div>
            </div>
          </div>
        }
        type="info"
        showIcon
        className="mb-4"
      />

      {/* 数据表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps || 10)
            },
          }}
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* 编辑模态框 */}
      <Modal
        title="编辑场次价格"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={600}
        destroyOnClose
      >
        {editingRecord && (
          <Alert
            message={editingRecord.title}
            description={`${editingRecord.session_date} ${editingRecord.start_time}`}
            type="info"
            className="mb-4"
          />
        )}
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="original_price"
              label="原价"
              rules={[{ required: true, message: '请输入原价' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                prefix="¥"
              />
            </Form.Item>

            <Form.Item
              name="vip_price"
              label="VIP价"
              rules={[{ required: true, message: '请输入VIP价' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                prefix="¥"
              />
            </Form.Item>

            <Form.Item
              name="discount_price"
              label="折扣价"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                prefix="¥"
                placeholder="留空表示不设置"
              />
            </Form.Item>

            <Form.Item
              name="vip_discount_price"
              label="VIP折扣价"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                prefix="¥"
                placeholder="留空表示不设置"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="discount_enabled"
            label="启用折扣时段"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="启用"
              unCheckedChildren="禁用"
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.discount_enabled !== currentValues.discount_enabled
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('discount_enabled') ? (
                <div className="grid grid-cols-2 gap-4">
                  <Form.Item
                    name="discount_start_time"
                    label="折扣开始时间"
                    rules={[{ required: true, message: '请选择开始时间' }]}
                  >
                    <TimePicker
                      style={{ width: '100%' }}
                      format="HH:mm"
                    />
                  </Form.Item>

                  <Form.Item
                    name="discount_end_time"
                    label="折扣结束时间"
                    rules={[{ required: true, message: '请选择结束时间' }]}
                  >
                    <TimePicker
                      style={{ width: '100%' }}
                      format="HH:mm"
                    />
                  </Form.Item>
                </div>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SessionPricingPage
